import {CUSTOM_TIER_DESCRIPTIONS, CUSTOM_MATERIALS_NOTE} from '../../shared/customTierCopy.js'
import { tierAdjustedTotal, type BlissBuildTierId } from './blissQuoteTiers.js'

/** Tiers shown on client proposals (order is computed — see sortLuxuryProposalTiers). */
export const BLISS_LUXURY_PROPOSAL_TIER_IDS = ['essential', 'reserve', 'signature'] as const
export type BlissLuxuryProposalTierId = (typeof BLISS_LUXURY_PROPOSAL_TIER_IDS)[number]

/**
 * Display order: **highest price → lowest** (top to bottom).
 *
 * Why: Luxury proposals anchor on the full vision first (price anchoring). Clients
 * mentally compare down from Signature/Reserve rather than up from Essential—so
 * mid tiers feel like smart savings, not sticker shock. Matches Bliss PDF proposals
 * (flagship package first) and keeps “Recommended” visible after the anchor tier.
 */
export function sortLuxuryProposalTiers(
  reserveBaselineTotal: number,
  courtesyDiscountPercent = 0,
): BlissLuxuryProposalTierId[] {
  return [...BLISS_LUXURY_PROPOSAL_TIER_IDS].sort(
    (a, b) =>
      proposalTierTotal(reserveBaselineTotal, b, courtesyDiscountPercent) -
      proposalTierTotal(reserveBaselineTotal, a, courtesyDiscountPercent),
  )
}

/**
 * Client-facing figures are quoted in whole hundreds. Multiplier math lands on amounts
 * like $15,867 that read as machine output rather than a considered proposal price.
 */
const PROPOSAL_PRICE_INCREMENT = 100

export function roundProposalPrice(amount: number): number {
  return Math.round(amount / PROPOSAL_PRICE_INCREMENT) * PROPOSAL_PRICE_INCREMENT
}

export function proposalTierTotal(
  reserveBaselineTotal: number,
  tierId: BlissLuxuryProposalTierId,
  courtesyDiscountPercent = 0,
): number {
  const factor = courtesyDiscountPercent > 0 ? 1 - courtesyDiscountPercent / 100 : 1
  return roundProposalPrice(tierAdjustedTotal(reserveBaselineTotal, tierId) * factor)
}

/** Default selection: Signature (recommended premium tier). */
export const BLISS_LUXURY_DEFAULT_TIER: BlissLuxuryProposalTierId = 'signature'

export type BlissLuxuryTierPresentation = {
  displayName: string
  recommended?: boolean
  /** Short paragraph; omit when using bullets only */
  description?: string
  bullets?: string[]
}

export const BLISS_LUXURY_TIER_PRESENTATION: Record<
  'reserve' | 'signature' | 'essential',
  BlissLuxuryTierPresentation
> = {
  reserve: {displayName: 'Reserve', description: CUSTOM_TIER_DESCRIPTIONS.reserve},
  signature: {displayName: 'Signature', recommended: true, description: CUSTOM_TIER_DESCRIPTIONS.signature},
  essential: {displayName: 'Essential', description: CUSTOM_TIER_DESCRIPTIONS.essential},
}

export const BLISS_PROJECT_DETAILS_FINE_PRINT = [
  CUSTOM_MATERIALS_NOTE,
  'Pricing is based on current material costs and is subject to change if material costs increase by more than 5% before fabrication begins.',
  'Finishing follows the selections listed in your room scope for every package. Unfinished wood applies only when selected; unlisted finish changes are quoted separately.',
  'Pricing does not include bedding, mattresses, décor, electrical work, or site modifications.',
  'Delivery and professional installation are included in tier totals shown when listed on your quote.',
] as const


